import { computed } from 'vue'
// types
import type { ComputedRef } from 'vue'
import type { BlockTestPickerRowProps } from '@ts-blocks/block-test-picker-row/interfaces'
import type { BrandShapeIconColor, BrandShapeIconShape } from '@UI/Brand/BrandShapeIcon'
// hooks
import { useEventsSection } from '@hooks/events/sections/events-section.hook'

const START_TEST_LABEL = 'Start Test'

interface TestDisciplineVisual {
  shape: BrandShapeIconShape
  color: BrandShapeIconColor
  glyph: string
}

// the icon is fixed by discipline per Figma (not editable in the admin)
const DISCIPLINE_VISUALS: Record<'math' | 'ela', TestDisciplineVisual> = {
  math: { shape: 'hexagon', color: 'ultraviolet', glyph: 'ruler-triangle' },
  ela: { shape: 'speech-bubble', color: 'sky', glyph: 'book-open-reader' },
}

interface BlockTestPickerRowHookOptions {
  block: () => BlockTestPickerRowProps
}

interface BlockTestPickerRowHookReturn {
  refDiscipline: ComputedRef<'math' | 'ela'>
  refVisual: ComputedRef<TestDisciplineVisual>
  refHref: ComputedRef<string>
  buttonLabel: string
  handleClick: () => void
}

export const useBlockTestPickerRow = ({ block }: BlockTestPickerRowHookOptions): BlockTestPickerRowHookReturn => {
  const { buildCtaHref, trackSectionEvent } = useEventsSection({ sectionName: 'test' })

  const refDiscipline = computed<'math' | 'ela'>(() => block().discipline ?? 'math')
  const refVisual = computed<TestDisciplineVisual>(() => DISCIPLINE_VISUALS[refDiscipline.value])

  const refHref = computed<string>(() => buildCtaHref(block().url ?? '#', { text: START_TEST_LABEL }))

  const handleClick = (): void => {
    trackSectionEvent('Button_click', { name: 'start_test', text: START_TEST_LABEL, url: refHref.value })
  }

  return {
    refDiscipline,
    refVisual,
    refHref,
    buttonLabel: START_TEST_LABEL,
    handleClick,
  }
}
